import { Helmet } from "react-helmet-async";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PageHero } from "@/components/shared/PageHero";
import { CTASection } from "@/components/shared/CTASection";
import { Calendar, Clock, ArrowLeft, Phone, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { staticBlogPosts, defaultBlogPostImage } from "./Blog";
import { format } from "date-fns";
import { Link as RouterLink } from "react-router-dom";

const blogContent: Record<string, { content: string[] }> = {
  "spring-lawn-care-checklist": {
    content: [
      "Spring is an exciting time for Minnesota homeowners. After months of snow and cold, it's time to get your lawn ready for the growing season. Here's your complete spring lawn care checklist.",
      "**1. Clean Up Winter Debris**",
      "Start by removing fallen branches, leaves, and any debris that accumulated over winter. This allows sunlight and air to reach your grass and prevents mold and disease.",
      "**2. Rake and Dethatch**",
      "Gently rake your lawn to remove dead grass and thatch buildup. If thatch is more than ½ inch thick, consider dethatching to allow water and nutrients to penetrate the soil.",
      "**3. Test Your Soil**",
      "A soil test tells you exactly what nutrients your lawn needs. Minnesota soils often need lime to balance pH levels. Test kits are available at garden centers or through your local extension office.",
      "**4. Apply Pre-Emergent Herbicide**",
      "Apply pre-emergent weed control before soil temperatures reach 55°F consistently. This prevents crabgrass and other annual weeds from germinating.",
      "**5. Fertilize Appropriately**",
      "Apply a balanced spring fertilizer to give your lawn the nutrients it needs to green up and grow strong. Avoid over-fertilizing, which can damage your lawn.",
      "**6. Overseed Bare Spots**",
      "Spring is a good time to fill in bare or thin areas. Prepare the soil, apply quality grass seed, and keep it consistently moist until established.",
      "**7. Check Your Irrigation System**",
      "If you have an irrigation system, have it inspected and turned on. Check for broken heads, leaks, and proper coverage.",
      "**8. Mow When Ready**",
      "Begin mowing when grass reaches about 3 inches. Set your mower high—never remove more than one-third of the blade height at once.",
      "**Need Help?**",
      "Our spring lawn care services take the guesswork out of getting your lawn ready. Contact us for a free estimate!",
    ],
  },
  "benefits-of-mulching": {
    content: [
      "Mulching is one of the simplest yet most effective things you can do for your landscape. Here are five key benefits of professional mulching.",
      "**1. Moisture Retention**",
      "Mulch acts as a barrier, reducing water evaporation from the soil. This means less watering and healthier plants, especially during Minnesota's hot summer months.",
      "**2. Weed Suppression**",
      "A proper layer of mulch (2-4 inches) blocks sunlight from reaching weed seeds, dramatically reducing weed growth. This means less time weeding and a cleaner-looking landscape.",
      "**3. Temperature Regulation**",
      "Mulch insulates plant roots from temperature extremes. In summer, it keeps roots cool. In winter, it protects them from freezing and thawing cycles that can damage plants.",
      "**4. Soil Health Improvement**",
      "As organic mulch breaks down, it adds nutrients to the soil and improves soil structure. This creates a healthier environment for plant roots and beneficial soil organisms.",
      "**5. Aesthetic Appeal**",
      "Fresh mulch gives your landscape beds a polished, well-maintained appearance. It provides a clean contrast to plants and defines bed edges beautifully.",
      "**Choosing the Right Mulch**",
      "• **Hardwood Mulch**: Classic choice, breaks down slowly, enriches soil",
      "• **Cedar Mulch**: Natural pest resistance, pleasant aroma, longer lasting",
      "• **Colored Mulch**: Available in red, black, and brown for design flexibility",
      "• **Rubber Mulch**: Longest lasting, good for playgrounds, doesn't decompose",
      "**Professional Installation Matters**",
      "Proper mulch installation includes bed preparation, weed barrier consideration, correct depth application, and keeping mulch away from plant stems and tree trunks. Our team ensures your mulching project is done right.",
    ],
  },
  "when-to-aerate-lawn": {
    content: [
      "Lawn aeration is essential for maintaining a healthy, thick lawn in Minnesota. But timing is everything. Here's when and why you should aerate.",
      "**What Is Aeration?**",
      "Core aeration removes small plugs of soil from your lawn, creating channels for air, water, and nutrients to reach grass roots. It relieves soil compaction and promotes healthier grass growth.",
      "**Best Time to Aerate in Minnesota**",
      "For cool-season grasses (Kentucky bluegrass, fescue) that dominate Minnesota lawns, the best time to aerate is **late August through September**.",
      "Why fall? Grass is actively growing, soil is still warm, and the lawn has time to recover before winter. Aerating in fall also gives you the perfect opportunity to overseed.",
      "**Spring Aeration: Proceed With Caution**",
      "While spring aeration is possible, it's generally less ideal because:",
      "• Disrupted soil can bring weed seeds to the surface",
      "• Spring is prime time for crabgrass germination",
      "• Grass is still emerging from dormancy",
      "If you must aerate in spring, do so early (April) and apply pre-emergent herbicide after the soil has settled.",
      "**Signs Your Lawn Needs Aeration**",
      "• Heavy clay soil (common in Minnesota)",
      "• Lawn gets heavy foot traffic",
      "• Thatch layer exceeds ½ inch",
      "• Water puddles or runs off instead of soaking in",
      "• Grass struggles despite proper watering and fertilization",
      "**How Often to Aerate**",
      "Most Minnesota lawns benefit from annual aeration. Lawns with heavy clay soil or high traffic may need aeration twice per year.",
      "**Combine With Overseeding**",
      "Fall aeration paired with overseeding is a powerful combination. The seed falls into the aeration holes, making excellent soil contact for quick germination.",
    ],
  },
  "hardscaping-curb-appeal": {
    content: [
      "Hardscaping—patios, walkways, retaining walls, and other non-plant landscape elements—can dramatically transform your outdoor space. Here's how.",
      "**Define Outdoor Living Spaces**",
      "A well-designed patio creates an outdoor room for entertaining, dining, or relaxing. It extends your living space and provides a destination in your yard.",
      "**Solve Drainage and Erosion Issues**",
      "Retaining walls do more than look attractive—they manage slopes, prevent erosion, and can even create terraced garden spaces on difficult terrain.",
      "**Create Welcoming Entryways**",
      "A beautiful walkway guides visitors to your front door while adding instant curb appeal. Pavers, natural stone, or stamped concrete can complement any home style.",
      "**Add Functional Beauty**",
      "• **Fire Pits**: Extend your outdoor season into cool Minnesota evenings",
      "• **Outdoor Kitchens**: Perfect for summer entertaining",
      "• **Seat Walls**: Provide extra seating and define spaces",
      "• **Water Features**: Add sound and visual interest",
      "**Increase Property Value**",
      "Quality hardscaping is a smart investment. Studies show outdoor living improvements can return 50-80% of their cost at resale, while significantly improving daily enjoyment of your home.",
      "**Low Maintenance Beauty**",
      "Unlike lawns and gardens, hardscape features require minimal maintenance. No mowing, watering, or pruning—just occasional cleaning and sealing.",
      "**Material Options**",
      "• **Concrete Pavers**: Versatile, durable, many colors and patterns",
      "• **Natural Stone**: Timeless beauty, unique character",
      "• **Brick**: Classic appeal, excellent durability",
      "• **Decorative Gravel**: Budget-friendly, permeable drainage",
      "**Professional Design Matters**",
      "Proper hardscape installation requires attention to base preparation, drainage, and long-term durability. Our team ensures your investment lasts for decades.",
    ],
  },
  "snow-removal-tips-homeowners": {
    content: [
      "Minnesota winters mean serious snow. Here's how to handle it safely—and when to call in the professionals.",
      "**Safety First**",
      "Snow removal can be strenuous work. Take breaks, stay hydrated, and listen to your body. If you have heart conditions or other health concerns, consider hiring help.",
      "**Shoveling Tips**",
      "• **Start Early**: Fresh snow is lighter than packed snow",
      "• **Push, Don't Lift**: Whenever possible, push snow rather than lifting it",
      "• **Lift With Your Legs**: When you must lift, bend at the knees, not the waist",
      "• **Small Loads**: Take smaller scoops to reduce strain",
      "• **Clear Frequently**: Multiple passes during a storm are easier than one massive effort",
      "**Protect Your Driveway**",
      "• Avoid metal shovels on concrete or asphalt—they can cause damage",
      "• Use ice melt products sparingly; they can harm concrete and plants",
      "• Consider calcium chloride for very cold temperatures (effective to -25°F)",
      "**Snow Blower Safety**",
      "• Never clear jams with your hands—use a stick or the tool provided",
      "• Add fuel before starting, never while running",
      "• Be aware of what's under the snow (newspapers, doormats, extension cords)",
      "**When to Call Professionals**",
      "Consider professional snow removal if:",
      "• You have a large driveway or parking area",
      "• Physical limitations prevent safe shoveling",
      "• Your schedule doesn't allow prompt clearing",
      "• You need reliable service for a business",
      "**Commercial Snow Removal**",
      "For businesses, prompt snow removal isn't just convenient—it's a liability issue. We offer commercial contracts to keep your property safe and accessible all winter.",
    ],
  },
  "fertilization-schedule-minnesota": {
    content: [
      "A proper fertilization schedule is key to maintaining a thick, healthy Minnesota lawn. Here's your complete guide to fertilizing throughout the year.",
      "**Understanding Fertilizer Numbers**",
      "Fertilizer labels show three numbers (e.g., 20-5-10) representing Nitrogen-Phosphorus-Potassium (N-P-K). Nitrogen promotes green growth, phosphorus supports roots, and potassium helps overall health.",
      "**Early Spring (April)**",
      "Apply a balanced fertilizer with pre-emergent weed control. This feeds emerging grass while preventing crabgrass. Use about 1 pound of nitrogen per 1,000 square feet.",
      "**Late Spring (May-June)**",
      "A second application supports active growth. Consider a slow-release formula that feeds gradually over 6-8 weeks.",
      "**Summer (July-August)**",
      "Go easy in summer heat. If you fertilize, use a light application. Too much nitrogen during hot weather can stress your lawn. Focus on watering properly instead.",
      "**Early Fall (September)**",
      "This is the most important fertilization of the year. Apply a high-nitrogen fertilizer to promote recovery from summer stress and encourage root development before winter.",
      "**Late Fall (October-November)**",
      "Apply a winterizer fertilizer high in potassium. This strengthens roots and helps your lawn survive winter, leading to earlier green-up in spring.",
      "**Tips for Success**",
      "• Water after fertilizing to activate the product",
      "• Never fertilize drought-stressed grass",
      "• Follow label directions—more is not better",
      "• Keep fertilizer off sidewalks and driveways to prevent runoff",
      "• Store products in a dry place",
      "**Professional Fertilization Programs**",
      "Our lawn care programs take the guesswork out of fertilization. We apply the right products at the right time, customized for Minnesota conditions.",
    ],
  },
  "choosing-plants-minnesota": {
    content: [
      "Choosing plants that thrive in Minnesota's climate is essential for a beautiful, low-maintenance landscape. Here are our top recommendations.",
      "**Understanding Hardiness Zones**",
      "Most of Minnesota falls in USDA Hardiness Zones 3-4, meaning plants must survive winter lows of -30°F to -40°F. Always check plant tags for zone ratings.",
      "**Native Plants**",
      "Native plants are adapted to our climate and support local wildlife:",
      "• **Black-Eyed Susan**: Bright yellow blooms, drought tolerant",
      "• **Purple Coneflower**: Attracts pollinators, medicinal properties",
      "• **Wild Bergamot**: Fragrant, loved by butterflies and bees",
      "• **Prairie Dropseed**: Ornamental grass with fall color",
      "**Reliable Shrubs**",
      "• **Hydrangea (Annabelle, Limelight)**: Stunning summer blooms",
      "• **Lilac**: Classic fragrance, extremely cold hardy",
      "• **Dogwood (Red Twig)**: Winter interest with colored stems",
      "• **Juniper**: Evergreen, drought tolerant, low maintenance",
      "**Trees for Minnesota**",
      "• **Maple (Red, Sugar, Norway)**: Beautiful fall color",
      "• **Oak (Bur, Red, White)**: Long-lived, wildlife value",
      "• **Spruce (Colorado Blue, White)**: Evergreen screening",
      "• **Apple/Crabapple**: Spring flowers, fall fruit",
      "**Perennials That Return Strong**",
      "• **Hosta**: Shade tolerant, many varieties",
      "• **Daylily**: Tough, colorful, low maintenance",
      "• **Sedum**: Drought tolerant, attracts butterflies",
      "• **Russian Sage**: Silvery foliage, blue flowers",
      "**Design Tips**",
      "• Layer plants by height: trees, shrubs, perennials",
      "• Mix textures and colors for visual interest",
      "• Consider bloom times for season-long color",
      "• Group plants with similar water needs together",
    ],
  },
  "lawn-mowing-mistakes": {
    content: [
      "Mowing seems simple, but common mistakes can damage your lawn. Here are seven mowing errors to avoid for a healthier, better-looking lawn.",
      "**1. Cutting Too Short**",
      "Scalping your lawn weakens grass and encourages weeds. Most Minnesota grasses should be kept at 3-3.5 inches. Taller grass shades out weeds and develops deeper roots.",
      "**2. Using Dull Blades**",
      "Dull mower blades tear grass instead of cutting cleanly, leaving ragged brown tips. Sharpen blades at least once per season—more if you have a large lawn.",
      "**3. Mowing Wet Grass**",
      "Wet grass clumps together, causing uneven cuts and potentially spreading disease. It also clogs your mower and can leave unsightly clumps on your lawn.",
      "**4. Removing Too Much at Once**",
      "Never cut more than one-third of the grass blade at a time. If grass gets too long between mowings, gradually bring it back down over multiple sessions.",
      "**5. Mowing the Same Pattern Every Time**",
      "Mowing the same direction creates ruts and causes grass to lean one way. Alternate your mowing pattern each time—this produces healthier, more upright grass.",
      "**6. Mowing at the Wrong Time**",
      "Avoid mowing during the hottest part of the day when grass is stressed. Early evening is ideal—grass has recovered from heat and has overnight to heal before the next day's sun.",
      "**7. Bagging Clippings Unnecessarily**",
      "Grass clippings return valuable nutrients to your lawn. Unless clippings are excessive or diseased, leave them on the lawn. They'll break down quickly and won't cause thatch.",
      "**Bonus Tip: Keep Up With Maintenance**",
      "Check mower oil, clean the deck, and inspect belts regularly. A well-maintained mower delivers better results and lasts longer.",
      "**Too Busy to Mow Right?**",
      "Our weekly mowing service ensures your lawn is cut at the right height, with the right equipment, at the right time. Contact us for a free quote!",
    ],
  },
};

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  
  // Try to fetch from database first
  const { data: dbPost, isLoading } = useQuery({
    queryKey: ['blog-post', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('slug', slug)
        .eq('published', true)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
  });

  // Get static post if not in database
  const staticPost = staticBlogPosts.find(p => p.slug === slug);
  const staticContent = slug ? blogContent[slug] : null;
  
  // Use database post if available, otherwise use static post
  const post = dbPost || staticPost;
  const content = dbPost?.content || staticContent?.content || [];

  // Get related posts (other posts in same category)
  const relatedPosts = staticBlogPosts
    .filter(p => p.slug !== slug && p.category === post?.category)
    .slice(0, 3);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-secondary" />
      </div>
    );
  }

  if (!post) {
    return (
      <>
        <Helmet>
          <title>Post Not Found | Riekels Lawn Care</title>
        </Helmet>
        <main>
          <PageHero
            badge="Blog"
            title="Post Not Found"
            subtitle="The article you're looking for doesn't exist."
          />
          <div className="container mx-auto px-4 py-12 text-center">
            <Link to="/blog" className="text-secondary hover:underline">
              ← Back to Blog
            </Link>
          </div>
        </main>
      </>
    );
  }

  // Parse content (handle both array and string formats)
  const contentArray = Array.isArray(content) ? content : 
    typeof content === 'string' ? content.split('\n\n') : [];

  return (
    <>
      <Helmet>
        <title>{post.title} | Riekels Lawn Care</title>
        <meta name="description" content={post.excerpt} />
      </Helmet>
      <main>
        {/* Hero Section */}
        <div className="relative h-[40vh] min-h-[300px] flex items-end">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${post.image_url || defaultBlogPostImage})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
          <div className="container mx-auto px-4 pb-8 relative z-10">
            <Link 
              to="/blog" 
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
            <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-secondary text-secondary-foreground mb-4">
              {post.category}
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white max-w-4xl">
              {post.title}
            </h1>
            <div className="flex items-center gap-4 mt-4 text-white/70 text-sm">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {format(new Date(post.created_at), 'MMMM d, yyyy')}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.read_time}
              </span>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Main Content */}
              <div className="lg:col-span-2">
                <article className="prose prose-lg max-w-none">
                  {contentArray.map((paragraph, index) => {
                    // Handle bold text marked with **
                    if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                      const text = paragraph.slice(2, -2);
                      return (
                        <h2 key={index} className="text-2xl font-heading font-bold text-foreground mt-8 mb-4">
                          {text}
                        </h2>
                      );
                    }
                    
                    // Handle bullet points
                    if (paragraph.startsWith('• ') || paragraph.startsWith('- ')) {
                      const text = paragraph.slice(2);
                      // Check for bold text within bullet
                      if (text.includes('**')) {
                        const parts = text.split('**');
                        return (
                          <p key={index} className="text-muted-foreground leading-relaxed ml-4 mb-2">
                            • {parts.map((part, i) => 
                              i % 2 === 1 ? <strong key={i} className="text-foreground">{part}</strong> : part
                            )}
                          </p>
                        );
                      }
                      return (
                        <p key={index} className="text-muted-foreground leading-relaxed ml-4 mb-2">
                          • {text}
                        </p>
                      );
                    }

                    // Handle inline bold text
                    if (paragraph.includes('**')) {
                      const parts = paragraph.split('**');
                      return (
                        <p key={index} className="text-muted-foreground leading-relaxed mb-6">
                          {parts.map((part, i) => 
                            i % 2 === 1 ? <strong key={i} className="text-foreground">{part}</strong> : part
                          )}
                        </p>
                      );
                    }

                    return (
                      <p key={index} className="text-muted-foreground leading-relaxed mb-6">
                        {paragraph}
                      </p>
                    );
                  })}
                </article>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="sticky top-24 space-y-6">
                  {/* Contact Card */}
                  <div className="bg-primary text-primary-foreground rounded-xl p-6">
                    <h3 className="text-xl font-heading font-bold mb-4">
                      Need Professional Help?
                    </h3>
                    <p className="text-primary-foreground/90 mb-6">
                      Our lawn care experts are ready to help with all your lawn and landscaping needs.
                    </p>
                    <div className="space-y-3">
                      <RouterLink to="/contact">
                        <Button className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold">
                          Get a Free Quote <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </RouterLink>
                      <a href="tel:+17632457813" className="block">
                        <Button variant="outline" className="w-full border-white/50 bg-white/10 text-white hover:bg-white/20">
                          <Phone className="w-4 h-4 mr-2" /> (763) 245-7813
                        </Button>
                      </a>
                    </div>
                  </div>

                  {/* Related Posts */}
                  {relatedPosts.length > 0 && (
                    <div className="bg-card rounded-xl border border-border p-6">
                      <h3 className="text-lg font-heading font-bold text-foreground mb-4">
                        Related Articles
                      </h3>
                      <div className="space-y-4">
                        {relatedPosts.map((relatedPost) => (
                          <RouterLink
                            key={relatedPost.slug}
                            to={`/blog/${relatedPost.slug}`}
                            className="block group"
                          >
                            <h4 className="text-foreground group-hover:text-secondary transition-colors font-medium text-sm mb-1">
                              {relatedPost.title}
                            </h4>
                            <span className="text-xs text-muted-foreground">
                              {relatedPost.read_time}
                            </span>
                          </RouterLink>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <CTASection />
      </main>
    </>
  );
};

export default BlogPost;
